import { useContext, memo, useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import history from '@history';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import WdPageSimple from '@main/core/WdPageSimple';
import {
  ButtonWithLoader,
  ComposedHeader,
  FormCardLayout,
  ImagePicker,
  ListCheckers,
  PopList,
} from '@main/components';
import { showMessage } from 'app/store/core/messageSlice';
import { getAdminById, getScopes, updateAdmin } from 'app/api/admin';
import { useWithApiValidation, useRequest } from '@main/hooks';
import { validateEmail, validatePhone } from 'app/api/validations';
import { Divider, Grid } from '@mui/material';
import { uploadImageFile } from 'src/app/api/image';
import InfoIcon from '@mui/icons-material/Info';
import { PhoneCodeSelect } from 'src/app/components';
import { schema } from '../Validators/update';
import { defaultValues } from '../Defaults/update';
import { imageDefaults } from '../Defaults/image';

const tooltipMsgIsActive =
  'Habilite esta opción si desea que el administrador esté activo en el sistema o cambie posicion para desactivar el registro de administrador.';

const Root = styled(WdPageSimple)(({ theme }) => ({
  backgroundColor: '#FFF',
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const IconInfo = styled(InfoIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.secondary.main,
  },
}));

const PermissionsCollection = memo(({ field, errors, catalogScopes, ...props }) => {
  return (
    <Grid {...props} container spacing={1} className="mb-8 flex justify-center items-start">
      <Grid item xs={12}>
        <ListCheckers
          {...field}
          horizontal
          selectAllChecker
          selectAllLabel="Agregar todos los permisos"
          labelPlacement="end"
          fieldset
          onChange={field.onChange}
          values={field.value}
          className="pt-0 w-full flex"
          innerClassName="flex justify-center flex-row"
          data={[]}
        />
      </Grid>
      {catalogScopes.map((catalog, index) => {
        const arrCatalog = Object.keys(catalog.scopes).map((key) => ({
          value: key,
          label: catalog.scopes[key],
        }));
        return (
          <Grid
            item
            key={`label_${catalog?.label}_${index}`}
            xs={12}
            md={6}
            className="flex justify-center items-center flex-col"
          >
            <ListCheckers
              {...field}
              fieldset
              onChange={field.onChange}
              values={field.value}
              className="w-full flex"
              title={catalog?.label}
              description={catalog?.description}
              error={errors}
              data={arrCatalog}
            />
          </Grid>
        );
      })}
    </Grid>
  );
});

export const Update = () => {
  const { id: previousId } = useParams();
  const Dispatch = useDispatch();
  const { finalizeSession } = useContext(BootstrapContext);
  const { config } = useSelector((state) => state.config);
  const { api } = config;
  const [metadata, setMetadata] = useState({});
  const [metadataError, setMetadataError] = useState(false);
  const [isImageDirty, setIsImageDirty] = useState(false);

  const [catalogScopes, setCatalogScopes] = useState([]);
  const [avatar, setAvatar] = useState(undefined);
  const [previousAvatar, setPreviousAvatar] = useState();
  const [previousPhone, setPreviousPhone] = useState();
  const [previousEmail, setPreviousEmail] = useState();
  const [previousPhoneCode, setPreviousPhoneCode] = useState('+52');

  // ----- Inicializacion de validadores de formulario
  const { control, formState, handleSubmit, setValue } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, dirtyFields, errors } = formState;

  // ------ Request Get Admin ------
  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };
  const requestOptions = {
    onError: handleOnError,
  };
  const { makeRequest: getPreviousData, response: responsePreviousData } = useRequest(
    getAdminById,
    {
      onForbidden: handleOnForbidden,
      onError: handleOnError,
    }
  );
  // ------ Request Update Admin ------
  const { makeRequest: tryUpdate, isLoading } = useRequest(updateAdmin, {
    onError: handleOnError,
  });
  // ------- Request Get Scopes Admin -----------
  const { makeRequest: tryGetScopes, response: responseGetScopes } = useRequest(getScopes, {
    onError: handleOnError,
  });
  // ------ Funcion para cargar los datos obtenidos de request
  const handleLoadToForm = (previousData) => {
    setPreviousAvatar(previousData?.avatar);
    setPreviousPhone(previousData.phone);
    setPreviousEmail(previousData.email);
    const result = _.omit(previousData, [
      'updated_at',
      'created_at',
      'id',
      'avatar',
      'image',
      'metadata',
    ]);
    Object.keys(result).forEach((keyName) => {
      if (keyName === 'phoneCode') {
        const phoneCode = result[keyName];
        const formattedPhoneCode = phoneCode
          ? phoneCode.startsWith('+')
            ? phoneCode
            : `+${phoneCode}`
          : '+52';
        setValue(keyName, formattedPhoneCode, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setPreviousPhoneCode(formattedPhoneCode);
        return;
      }
      setValue(keyName, result[keyName], { shouldValidate: true, shouldDirty: true });
    });
  };

  // ------- watchers scopes ---------
  useLayoutEffect(() => {
    tryGetScopes();
  }, []);

  useLayoutEffect(() => {
    if (responseGetScopes === undefined) return;
    setCatalogScopes(responseGetScopes);
  }, [responseGetScopes]);

  // ------ Realiza request para cargar información del admin
  useEffect(() => {
    if (previousId === undefined) return;
    getPreviousData(previousId);
  }, [previousId]);

  // ------ Escucha response de request de Admin
  useEffect(() => {
    console.log(responsePreviousData);
    if (responsePreviousData === undefined) return;
    handleLoadToForm(responsePreviousData);
  }, [responsePreviousData]);

  // ------ handlers ------------
  const handleLoadImage = (image) => {
    setAvatar(image);
  };

  const handleUpdate = (form) => {
    const builtForm = {
      ...form,
      avatar,
      metadata,
      phoneCode: form.phoneCode || previousPhoneCode,
    };
    console.log(JSON.stringify(builtForm, null, 3));
    tryUpdate(builtForm, previousId).then(({ isOk }) => {
      if (isOk) {
        Dispatch(
          showMessage({
            message: 'Información actualizada correctamente',
            variant: 'success',
          })
        );
        history.replace('/admin');
      }
    });
  };

  const {
    isUnique: uniqueEmail,
    errorMessage: errorMessageEmail,
    validateField: validateFieldEmail,
  } = useWithApiValidation('phone', validateEmail);

  const {
    isUnique: uniquePhone,
    errorMessage: errorMessagePhone,
    validateField: validateFieldPhone,
  } = useWithApiValidation('phone', validatePhone);

  const isDirty = isImageDirty || !_.isEmpty(dirtyFields);
  const disableButton = !isDirty || !isValid || metadataError;

  return (
    <Root
      header={<ComposedHeader decorator="button" />}
      content={
        <Box
          className="flex justify-center items-start"
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <FormCardLayout title="Editar administrador">
            <FormCardLayout.FullColum>
              <ImagePicker
                onAfterUpload={handleLoadImage}
                onDeleteUpload={() => setAvatar(null)}
                baseUri={api.host}
                storeRequest={uploadImageFile}
                formDataParams={imageDefaults}
                setImage={previousAvatar}
                onDirty={(newVal) => setIsImageDirty(newVal)}
                replaceOnly
              />
            </FormCardLayout.FullColum>
            <FormCardLayout.FullColum>
              <form
                autoComplete="off"
                name="adminUpdateForm"
                noValidate
                className="flex flex-col justify-center w-full"
              >
                <div className="flex flex-col">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Nombre"
                        autoFocus
                        type="text"
                        error={!!errors.name}
                        helperText={errors?.name?.message}
                        variant="outlined"
                        required
                        fullWidth
                        inputProps={{
                          form: {
                            autocomplete: 'off',
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Correo electrónico"
                        autoFocus
                        type="email"
                        error={!!errors.email || !uniqueEmail}
                        helperText={errors?.email?.message || errorMessageEmail}
                        variant="outlined"
                        fullWidth
                        required
                        disabled
                        onChange={(current) => {
                          field.onChange(current);
                          if (previousEmail === current.target.value.toString().toLowerCase())
                            return;
                          validateFieldEmail(current);
                        }}
                      />
                    )}
                  />
                  <Grid className="mb-32" spacing={2.5} container>
                    <Grid xs={3} item>
                      <Controller
                        name="phoneCode"
                        control={control}
                        render={({ field }) => (
                          <PhoneCodeSelect
                            {...field}
                            type="text"
                            error={!!errors.phoneCode}
                            helperText={errors?.phoneCode?.message}
                            variant="outlined"
                            required
                            fullWidth
                            inputProps={{
                              style: {
                                height: '5.3rem',
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid xs={9} item>
                      <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Número telefónico"
                            autoFocus
                            type="number"
                            error={!!errors.phone || !uniquePhone}
                            helperText={errors?.phone?.message || errorMessagePhone}
                            variant="outlined"
                            fullWidth
                            onChange={(current) => {
                              field.onChange(current);
                              if (current.target.value.toString() === previousPhone) return;
                              validateFieldPhone(current);
                            }}
                            InputProps={{
                              inputProps: {
                                style: {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                  MozAppearance: 'textfield',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Box className="flex flex-col justify-center items-center mb-32">
                        <FormLabel component="legend">Estatus del administrador</FormLabel>
                        <Box className="flex flex-row justify-center items-center gap-1">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value}
                                {...field}
                                inputProps={{ 'aria-label': 'Estatus' }}
                              />
                            }
                            label={field.value ? 'Habilitado' : 'Deshabilitado'}
                          />
                          <PopList label={<IconInfo />}>{tooltipMsgIsActive}</PopList>
                        </Box>
                      </Box>
                    )}
                  />
                  <Divider className="mb-8 mt-12">Permisos para administrador</Divider>
                  <Controller
                    name="scopes"
                    control={control}
                    render={({ field }) => (
                      <PermissionsCollection
                        field={field}
                        errors={!!errors.scopes}
                        catalogScopes={catalogScopes}
                      />
                    )}
                  />
                </div>
              </form>
            </FormCardLayout.FullColum>
            {/*             <FormCardLayout.FullColum>
              <div>
                <MetadataExpandForm
                  onChange={(metafields) => setMetadata(metafields)}
                  onError={(metaError) => setMetadataError(metaError)}
                  initialValue={responsePreviousData ? responsePreviousData.metadata : {}}
                />
              </div>
            </FormCardLayout.FullColum> */}
            <FormCardLayout.RowButton>
              <Button
                onClick={() => history.push('/admin')}
                variant="contained"
                className="w-full md:w-auto"
                color="info"
              >
                Cancelar
              </Button>
              <ButtonWithLoader
                onClick={handleSubmit(handleUpdate)}
                color="success"
                aria-label="Actualizar administrador"
                variant="contained"
                disabled={disableButton}
                type="submit"
                isLoading={isLoading}
              >
                Guardar
              </ButtonWithLoader>
            </FormCardLayout.RowButton>
          </FormCardLayout>
        </Box>
      }
    />
  );
};

export default Update;
